import { tsv } from "@faire/slate/lib/tsv";
import React, { useRef } from "react";

import { cn } from "../../../../lib/utils";

import { ValueHistogram } from "./ValueHistogram";

export type RangeValue = { min: number; max: number };

type KnobName = "min" | "max";

type RangeSliderProps = {
  rangeMin: number;
  rangeMax: number;
  step: number;
  value: RangeValue;
  onChange: (next: RangeValue) => void;
  /**
   * Called once the interaction settles — pointer release, a key press, or a
   * click on the track. Lets a caller treat the moves in between as a draft.
   */
  onCommit?: () => void;
  /** Adds a draggable lower knob. Single-knob sliders pin `min` to `rangeMin`. */
  dualKnob?: boolean;
  /** Splits the track into this many visible segments. Omit for a smooth track. */
  notchCount?: number;
  minKnobLabel: string;
  maxKnobLabel: string;
  /**
   * Distribution bars drawn above the track. Rendered here rather than by the
   * caller so they share the track's insets and line up with it exactly.
   */
  histogramBuckets?: number[];
  histogramHeightPx?: number;
};

const clamp = (value: number, low: number, high: number) =>
  Math.min(Math.max(value, low), high);

/*
 * A bare rail when bars sit above it, so the knob's overhang lands on them
 * (Figma draws the same 1px seam). Otherwise a full knob-height row.
 */
const trackRowStyle = tsv({
  base: "flex w-full items-center",
  variants: {
    hasHistogram: {
      true: "mt-px h-0.5",
      false: "h-7",
    },
  },
});

/*
 * Figma node 3118:100612, sized up to 28px on Sam's ask. The design ships
 * this as an SVG, but it is a plain circle — a hairline of #333 at 10% and a
 * soft drop shadow — so it is
 * styled here instead: an image would lose the focus ring, the cursor and
 * pointer capture that a slider handle needs. Neither the border tint nor the
 * shadow has a Slate token, hence the two arbitrary values.
 */
const knobStyle = cn(
  "absolute top-1/2 size-7 -translate-x-1/2 -translate-y-1/2 rounded-full",
  "bg-fs-surface-primary border border-[#3333331a] shadow-[0_4px_8px_rgba(0,0,0,0.15)]",
  "cursor-grab active:cursor-grabbing",
  // The hairline firms up to the primary colour while the knob is under the
  // cursor or being driven, so the handle reads as live.
  "hover:border-fs-text-primary active:border-fs-text-primary",
  "focus-visible:border-fs-text-primary focus-visible:outline-none"
);

export const RangeSlider: React.FC<RangeSliderProps> = ({
  rangeMin,
  rangeMax,
  step,
  value,
  onChange,
  onCommit,
  dualKnob = false,
  notchCount,
  minKnobLabel,
  maxKnobLabel,
  histogramBuckets,
  histogramHeightPx = 0,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingKnob = useRef<KnobName | null>(null);

  const hasHistogram = histogramBuckets !== undefined;
  const span = rangeMax - rangeMin;
  const minPercent = span === 0 ? 0 : ((value.min - rangeMin) / span) * 100;
  const maxPercent = span === 0 ? 0 : ((value.max - rangeMin) / span) * 100;
  const fillStart = dualKnob ? minPercent : 0;

  const snapToStep = (raw: number) => {
    const stepped = Math.round((raw - rangeMin) / step) * step + rangeMin;
    return clamp(stepped, rangeMin, rangeMax);
  };

  const valueFromClientX = (clientX: number) => {
    const track = trackRef.current;
    if (track === null) {
      return null;
    }
    const rect = track.getBoundingClientRect();
    if (rect.width === 0) {
      return null;
    }
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    return snapToStep(rangeMin + ratio * span);
  };

  // Knobs may never cross each other.
  const commit = (knob: KnobName, next: number) => {
    if (knob === "min") {
      onChange({ min: Math.min(next, value.max), max: value.max });
      return;
    }
    onChange({
      min: value.min,
      max: Math.max(next, dualKnob ? value.min : rangeMin),
    });
  };

  const handleKnobPointerDown =
    (knob: KnobName) => (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.focus();
      draggingKnob.current = knob;
    };

  const handleKnobPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const knob = draggingKnob.current;
    if (knob === null) {
      return;
    }
    const next = valueFromClientX(event.clientX);
    if (next !== null) {
      commit(knob, next);
    }
  };

  const handleKnobPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const wasDragging = draggingKnob.current !== null;
    draggingKnob.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (wasDragging) {
      onCommit?.();
    }
  };

  const handleKnobKeyDown =
    (knob: KnobName) => (event: React.KeyboardEvent<HTMLDivElement>) => {
      const current = knob === "min" ? value.min : value.max;
      let next = current;

      if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
        next = current - step;
      } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
        next = current + step;
      } else if (event.key === "Home") {
        next = rangeMin;
      } else if (event.key === "End") {
        next = rangeMax;
      } else {
        return;
      }

      event.preventDefault();
      // One notch per press, so there is nothing to batch — publish it now.
      commit(knob, clamp(next, rangeMin, rangeMax));
      onCommit?.();
    };

  // Clicking the bar jumps the nearer knob to that spot.
  const handleTrackPointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const next = valueFromClientX(event.clientX);
    if (next === null) {
      return;
    }
    if (!dualKnob) {
      commit("max", next);
      onCommit?.();
      return;
    }
    const nearerKnob =
      Math.abs(next - value.min) <= Math.abs(next - value.max) ? "min" : "max";
    commit(nearerKnob, next);
    // A click on the track is a single jump, not a drag.
    onCommit?.();
  };

  const notchDividers =
    notchCount !== undefined && notchCount > 1
      ? Array.from({ length: notchCount - 1 }, (_, index) => {
          const position = ((index + 1) / notchCount) * 100;
          return (
            <div
              key={position}
              aria-hidden
              className="bg-fs-surface-primary absolute top-0 h-full w-0.5"
              style={{ left: `${position}%` }}
            />
          );
        })
      : null;

  return (
    /*
     * A knob is centred on its position, so at either end half of it sits
     * outside the track. The rail is inset by that half-width (against
     * `size-7`) so knobs land flush with the surrounding content edges instead
     * of overhanging them. A single-knob slider has nothing at the low end, so
     * it is inset on the right only — padding both sides would indent the
     * track from the fields below it. Knobs nest inside the track so their
     * percentages stay relative to the rail they travel along.
     */
    <div className={cn("flex w-full flex-col pr-3.5", dualKnob && "pl-3.5")}>
      {!hasHistogram ? null : (
        <ValueHistogram
          buckets={histogramBuckets}
          heightPx={histogramHeightPx}
          selectionEnd={maxPercent / 100}
          selectionStart={fillStart / 100}
        />
      )}
      {/* With a histogram the track sits 1px under the bars and the row
          collapses to the rail itself, so the knob — centred on the rail and
          taller than it — rides over the bars. Without one the row keeps the
          knob's full height so nothing above it is overlapped. */}
      <div className={trackRowStyle.cls({ hasHistogram })}>
        <div
          ref={trackRef}
          onPointerDown={handleTrackPointerDown}
          className="bg-fs-border-muted relative h-0.5 w-full cursor-pointer rounded-full"
        >
          <div
            className="bg-fs-text-primary absolute top-0 h-full rounded-full"
            style={{
              left: `${fillStart}%`,
              width: `${Math.max(maxPercent - fillStart, 0)}%`,
            }}
          />
          {notchDividers}

          {dualKnob ? (
            <div
              role="slider"
              tabIndex={0}
              aria-label={minKnobLabel}
              aria-valuemin={rangeMin}
              aria-valuemax={value.max}
              aria-valuenow={value.min}
              onPointerDown={handleKnobPointerDown("min")}
              onPointerMove={handleKnobPointerMove}
              onPointerUp={handleKnobPointerUp}
              onPointerCancel={handleKnobPointerUp}
              onKeyDown={handleKnobKeyDown("min")}
              className={knobStyle}
              style={{ left: `${minPercent}%` }}
            />
          ) : null}

          <div
            role="slider"
            tabIndex={0}
            aria-label={maxKnobLabel}
            aria-valuemin={dualKnob ? value.min : rangeMin}
            aria-valuemax={rangeMax}
            aria-valuenow={value.max}
            onPointerDown={handleKnobPointerDown("max")}
            onPointerMove={handleKnobPointerMove}
            onPointerUp={handleKnobPointerUp}
            onPointerCancel={handleKnobPointerUp}
            onKeyDown={handleKnobKeyDown("max")}
            className={knobStyle}
            style={{ left: `${maxPercent}%` }}
          />
        </div>
      </div>
    </div>
  );
};
